import {
    ScheduleSubModes,
    isAccruedScheduleMode,
} from "@common/helpers/domains/accounting/accruedScheduleMode";
import { ScheduleTemplateListItem } from "@common/schemas/domains/accounting/scheduleTemplate.schema";
import { RecognitionAnchor } from "@common/types/domains/accounting/schedule";

export type SchedulePickerTemplateWithAnchors = Pick<
    ScheduleTemplateListItem,
    | "id"
    | "name"
    | "group"
    | "entityIds"
    | "anchors"
    | "hasSpecificDateAnchor"
    | "revenueSubMode"
    | "expenseSubMode"
>;

/**
 * A Specific-date template cannot resolve a start date from the source: apply
 * throws "A specific date is required by this schedule" unless one is supplied
 * (schedule-anchor-resolution.helper.ts). Source-date-only templates apply in
 * one click.
 */
export function requiresRecognitionStartDate(
    template: Pick<SchedulePickerTemplateWithAnchors, "hasSpecificDateAnchor">,
): boolean {
    return template.hasSpecificDateAnchor;
}

/**
 * Whether a one-click apply would post something the user has not seen. A
 * Specific date template needs its date, and an accrued template's default
 * start runs back from the source by the whole term, often into locked
 * months the user can only resolve by moving the start. Both open the
 * schedule modal first; a deferred source-date template applies in one click.
 */
export function opensScheduleModalBeforeApply(
    template: Pick<SchedulePickerTemplateWithAnchors, "hasSpecificDateAnchor"> &
        Partial<ScheduleSubModes>,
): boolean {
    return template.hasSpecificDateAnchor || isAccruedScheduleMode(template);
}

/**
 * Anchors an invoice source cannot resolve: bill-date anchors are rejected
 * outright for revenue schedules (backend
 * .../schedule-anchor-resolution.helper.ts). A transaction-date anchor is the
 * stored form of the editor's "Source date" and resolves from the invoice date.
 *
 * Returns why the template cannot be applied to an invoice, or null when it can.
 */
export function resolveInvoiceTemplateBlockReason(
    template: Pick<SchedulePickerTemplateWithAnchors, "anchors">,
): string | null {
    if (template.anchors.includes(RecognitionAnchor.billDate)) {
        return "Starts from a bill date, which revenue schedules do not support";
    }
    return null;
}
